import { useEffect, useRef } from "react";

/**
 * Quick Find (ADR-0009): page titles searched in the browser, from the sidebar's tree (only
 * pages the viewer may open), recent pages remembered per browser, and the actions people can
 * run from it.
 */

/** Lower case, without diacritics, quotes straight: "titulo" finds "Título", 'x' finds ‘x’. */
export const fold = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[“”„‟«»‹›]/gu, '"')
    .replace(/[‘’‚‛]/gu, "'");

const MAX_RECENT = 8;
const recentKey = (workspaceId: string) => `paginae:recent:${workspaceId}`;

/** Recently opened pages, newest first: a per-browser convenience, never relied on. */
export function loadRecent(workspaceId: string): string[] {
  try {
    const raw = localStorage.getItem(recentKey(workspaceId));
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

export function rememberPage(workspaceId: string, pageId: string) {
  const ids = [pageId, ...loadRecent(workspaceId).filter((id) => id !== pageId)];
  try {
    localStorage.setItem(recentKey(workspaceId), JSON.stringify(ids.slice(0, MAX_RECENT)));
  } catch {
    // Storage may be unavailable (private mode, blocked site data); Quick Find still works.
  }
}

/** A keyboard shortcut as keys, "Mod" being ⌘ on Apple devices and Ctrl elsewhere. */
export type Shortcut = string[];

export const isApple = () =>
  typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

const APPLE_KEYS: Record<string, string> = {
  Mod: "⌘",
  Shift: "⇧",
  Alt: "⌥",
  AltRight: "right ⌥",
  ArrowUp: "↑",
  ArrowDown: "↓",
  Enter: "↩",
};
const OTHER_KEYS: Record<string, string> = {
  Mod: "Ctrl",
  AltRight: "Right Alt",
  ControlRight: "Right Ctrl",
  ArrowUp: "↑",
  ArrowDown: "↓",
};

/** How a shortcut is shown: "⌘⇧↑" on Apple devices, "Ctrl+Shift+↑" elsewhere. */
export function shortcutLabel(keys: Shortcut, apple: boolean): string {
  const names = keys.map((k) => (apple ? APPLE_KEYS : OTHER_KEYS)[k] ?? k);
  return apple ? names.join("") : names.join("+");
}

/** A shortcut as this device shows it: "⌘Z" on Apple devices, "Ctrl+Z" elsewhere. */
export const keysLabel = (keys: Shortcut) => shortcutLabel(keys, isApple());

/** The same shortcut for aria-keyshortcuts: "Meta+Shift+ArrowUp" or "Control+Shift+ArrowUp". */
export function shortcutAria(keys: Shortcut, apple: boolean): string {
  const aria: Record<string, string> = {
    Mod: apple ? "Meta" : "Control",
    AltRight: "Alt",
    ControlRight: "Control",
  };
  return keys.map((k) => aria[k] ?? k).join("+");
}

/** Actions Quick Find offers besides pages; screens register theirs while they are open. */
export interface Command {
  id: string;
  label: string;
  /** Other words it is found by (localised and English), lower case. */
  keywords?: string[];
  shortcut?: Shortcut;
  /**
   * For the selected text (`selection`) or the link at the cursor (`link`): listed first, under
   * their own heading, when the palette opens (ADR-0048).
   */
  section?: "selection" | "link";
  /**
   * Where else the right-click menu lists it (ADR-0054): under "This block", with Cut, Copy and
   * Paste, or for the items chosen in a list.
   */
  context?: "block" | "clipboard" | "chosen";
  run: () => void;
}

/** Sources by ID, the latest first: a second editor (a peek's) stands in for the page's a while. */
const sources = new Map<string, (() => Command[])[]>();

/** Adds a source of commands (the editor's block actions, say) until the returned function runs. */
export function registerCommands(id: string, commands: () => Command[]): () => void {
  sources.set(id, [commands, ...(sources.get(id) ?? [])]);
  return () => {
    const rest = (sources.get(id) ?? []).filter((c) => c !== commands);
    if (rest.length) sources.set(id, rest);
    else sources.delete(id);
  };
}

export const registeredCommands = (): Command[] =>
  [...sources.values()].flatMap((c) => c[0]?.() ?? []);

/**
 * The commands of an item on the screen (a page in the sidebar), for the right-click menu
 * (ADR-0054): kept by the element that stands for the item, while it is shown.
 */
const items = new Map<Element, () => { title: string; commands: Command[] }>();

export function registerItemCommands(
  element: Element,
  commands: () => { title: string; commands: Command[] },
): () => void {
  items.set(element, commands);
  return () => {
    if (items.get(element) === commands) items.delete(element);
  };
}

/** The first registered item inside `container`, if any. */
export function itemCommandsIn(container: Element) {
  for (const [element, commands] of items) {
    if (container.contains(element)) return commands();
  }
  return null;
}

/**
 * A screen's actions, in the command palette while it is shown (ADR-0045): every action people
 * can take on a screen is also a command. The latest list is read when the palette opens.
 */
export function useCommands(id: string, commands: Command[]) {
  const latest = useRef(commands);
  useEffect(() => {
    latest.current = commands;
  });
  useEffect(() => registerCommands(id, () => latest.current), [id]);
}

/** Commands matching every word of the query in their label or keywords. */
export function findCommands(commands: Command[], query: string): Command[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  return commands.filter((c) => {
    const text = fold([c.label, ...(c.keywords ?? [])].join(" "));
    return words.every((w) => text.includes(w));
  });
}
